<template>
	<section class="cd-hwa">
		<div class="cd-hwa__header">
			<h2 class="cd-hwa__title">
				{{ homework_attempt.homework.lecture.name }}
			</h2>
			<div class="cd-hwa__meta cd-meta">
				<dl class="cd-meta__list">
					<div class="cd-meta__item">
						<dt>Компания</dt>
						<dd>{{ homework_attempt.user.company.name }}</dd>
					</div>
					<div class="cd-meta__item">
						<dt>Курс</dt>
						<dd>
							{{ homework_attempt.homework.lecture.course.name }} || {{ homework_attempt.homework.lecture.course_id }}
						</dd>
					</div>
					<div class="cd-meta__item">
						<dt>Имя студента</dt>
						<dd>{{ homework_attempt.user.name }}</dd>
					</div>
					<div class="cd-meta__item">
						<dt>Email студента</dt>
						<dd>
							<a :href="`${homework_attempt.user.email}`" target="_blank" rel="noopener noreferrer">{{ homework_attempt.user.email }}</a>
						</dd>
					</div>
					<div class="cd-meta__item">
						<dt>Дата отправки</dt>
						<dd>{{ formatDate(homework_attempt.created_at) }}</dd>
					</div>
				</dl>
			</div>
		</div>

		<section class="cd-hwa__section">
			<h3 class="cd-hwa__subtitle">Настройки</h3>
			<div class="cd-hwa__card">
				<div class="cd-hwa__attempt-type">
					<span class="label">Тип попытки:</span>
					<span class="value">
						{{ attemptTypeLabel }}
						<template v-if="homework_attempt.previous_reviewer_email">
							<span class="cd-hwa__prev-reviewer">(предыдущий проверяющий: <a :href="`mailto:${homework_attempt.previous_reviewer_email}`">{{ homework_attempt.previous_reviewer_email }}</a>)</span>
						</template>
					</span>
				</div>
				<div class="cd-hwa__group-field">
					<label class="labeled-field labeled-field--select flex-grow" for="id-homework-reviewer">
						<span class="labeled-field__label">Проверяет:</span>
						<span class="labeled-field__input-wrap">
							<select id="id-homework-reviewer" class="labeled-field__input" v-model="selectedReviewer" :disabled="homework_attempt.control">
								<option value="">Выберите проверяющего</option>
								<option v-for="reviewer in reviewers" :key="reviewer.id" :value="reviewer.id">
									{{ reviewer.email }}
								</option>
							</select>
						</span>
					</label>

					<label class="labeled-field labeled-field--select flex-grow" for="id-homework-crosschecker">
						<span class="labeled-field__label">Кроссчекает:</span>
						<span class="labeled-field__input-wrap">
							<select id="id-homework-crosschecker" class="labeled-field__input" v-model="selectedCrossChecker" :disabled="homework_attempt.control">
								<option value="">Выберите кроссчекера</option>
								<option v-for="checker in reviewers" :key="checker.id" :value="checker.id">
									{{ checker.email }}
								</option>
							</select>
						</span>
					</label>

					<label class="labeled-field labeled-field--select flex-grow" for="id-homework-status">
						<span class="labeled-field__label">Статус ДЗ:</span>
						<span class="labeled-field__input-wrap">
							<select id="id-homework-status" class="labeled-field__input" v-model="selectedStatus" :disabled="homework_attempt.control">
								<option v-for="status in statuses" :key="status.value" :value="status.value">
									{{ status.text }}
								</option>
							</select>
						</span>
					</label>

					<label class="toggle-switch">
						<input
							id="id-homework-skip-count"
							type="checkbox"
							v-model="selectedSkipCount"
						>
						<span class="slider"></span>
						<span class="label-text">Не учитывать при лимите попыток</span>
					</label>

					<label class="toggle-switch">
						<input
							id="id-homework-no-review-payment"
							type="checkbox"
							v-model="selectedNoReviewPayment"
						>
						<span class="slider"></span>
						<span class="label-text">Не хватает информации для проверки (не оплачивается)</span>
					</label>

					<label class="toggle-switch">
						<input
							id="id-homework-custom-case"
							type="checkbox"
							v-model="selectedCustomCase"
							:disabled="homework_attempt.control"
						>
						<span class="slider"></span>
						<span class="label-text">Кейс студента</span>
					</label>
				</div>
				<button @click="submitAllChanges" class="regular-btn light-blue-btn mt-10" type="button" :disabled="isButtonDisabled">
					Применить изменения
				</button>

				<p v-if="isButtonDisabled && (!selectedReviewer)" class="button-info">
					Пожалуйста, выберите и примените проверяющего
				</p>
			</div>
		</section>

		<section class="cd-hwa__section">
			<h3 class="cd-hwa__subtitle">Текст задания</h3>
			<div class="cd-hwa__card">
				<div class="homework-rich-text" v-html="homework_attempt.homework.body"></div>
			</div>
		</section>

		<section class="cd-hwa__section">
			<h3 class="cd-hwa__subtitle">Ответ пользователя</h3>
			<div class="cd-hwa__card">
				<template v-if="homework_attempt.answer || (homework_attempt.attachments && homework_attempt.attachments.length)">
					<div class="homework-rich-text" v-html="linkifyText(homework_attempt.answer)"></div>

					<template v-if="homework_attempt.attachments && homework_attempt.attachments.length">
						<h3 class="label mb-5 mt-20">Прикрепленные файлы</h3>
						<div class="files-preview-list">
							<div
								v-for="(attachment, index) in homework_attempt.attachments"
								:key="index"
								class="files-preview-list__item"
							>
								<span class="files-preview-list__check icon icon--check icon--tiny icon--high-color" />
								<a :href="attachment.file_url_original" target="_blank" rel="noopener noreferrer" class="files-preview-list__name">{{ attachment.file_name }}</a>
							</div>
						</div>
					</template>
				</template>
				<template v-else>
					Студент пока не прислал свой ответ
				</template>
			</div>
		</section>

		<section v-if="previous_attempts.length" class="cd-hwa__section">
		  <h3 class="cd-hwa__subtitle">Предыдущие попытки</h3>
		  <div class="cd-hwa__card">
		    <ul class="cd-hwa__prev-list">
		      <li
		        v-for="attempt in previous_attempts"
		        :key="attempt.url"
		        class="cd-hwa__prev-item"
		      >
		        <div class="d-inline-flex align-items-center flex-nowrap">
		          <a
		            :href="attempt.url"
		            target="_blank"
		            rel="noopener noreferrer"
		            class="cd-hwa__prev-link"
		          >
		            Попытка от {{ formatDate(attempt.created_at) }}
		          </a>
		          <div class="cd-hwa__prev-badges">
		            <span class="badge badge-sm badge-skip">
		              {{ attempt.skip_count ? 'Пропущена' : 'Учтена' }}
		            </span>
		            <span class="badge badge-sm badge-status">
		              {{ $t(`homework_statuses.${attempt.status}`) }}
		            </span>
		          </div>
		        </div>
		      </li>
		    </ul>
		  </div>
		</section>

		<section class="cd-hwa__section">
			<h3 class="cd-hwa__subtitle">Комментарии проверяющих</h3>
			<div class="cd-hwa__card">
				<button class="link-btn" @click="showComments">
					<template v-if="isShowComments">- Скрыть комментарии проверяющих</template>
					<template v-else>+ Показать комментарии проверяющих</template>
				</button>
				<div v-show="isShowComments" class="mt-20">
					<ul v-if="homework_attempt.homework_attempt_comments.length">
						<li v-for="(comment, index) in homework_attempt.homework_attempt_comments" :key="index" class="cd-comment">
							<div class="cd-comment__item">
								<h3 class="cd-comment__title">{{ comment.user.first_name }} {{ comment.user.last_name }}</h3>
								<div class="cd-comment__meta">
									<span>{{ comment.user.email }}</span>
									<span>{{ formatDate(comment.created_at) }}</span>
								</div>
								<div class="cd-comment__text">
									{{ comment.body }}
								</div>
							</div>
						</li>
					</ul>

					<div v-if="!homework_attempt.control" class="page-card__col page-card__col--main mt-30">
						<label class="labeled-field" for="id-homework-comment">
							<span class="labeled-field__label">Ваш комментарий:</span>
							<textarea
								id="id-homework-comment"
								class="labeled-field__input"
								v-model="attemptComment"
								placeholder="Напишите здесь свой комментарий"
							></textarea>
						</label>
						<button class="regular-btn light-blue-btn mt-10" @click="submitAttemptComment" type="button">Отправить комментарий коллегам</button>
					</div>
				</div>
			</div>
		</section>

		<section class="cd-hwa__section">
			<h3 class="cd-hwa__subtitle">Ваш ответ</h3>
			<div class="cd-hwa__card">
				<div v-if="isControlEditable" class="labeled-field">
					<span class="labeled-field__label">Ответ студенту:</span>
					<div class="labeled-field__input labeled-field__input--rich">
						<ckeditor
							v-model="editorData"
							:editor="editor"
							:config="editorConfig"
						/>
					</div>
				</div>
				<div v-if="isControlEditable" class="labeled-field mb-10" >
					<span class="labeled-field__label">Прикрепить файлы:</span>
					<input
						type="file"
						multiple
						class="labeled-field__input"
						@change="handleReviewerFileUpload"
						:disabled="!['success_done', 'failed_done'].includes(homework_attempt.status) || !selectedReviewer || selectedReviewer !== homework_attempt.reviewer_id"
					/>
					<div v-if="reviewerAttachments.length" class="mt-5">
						<div v-for="(file, idx) in reviewerAttachments" :key="idx">
							{{ file.name }}
						</div>
					</div>
				</div>
				<button v-if="isControlEditable" class="regular-btn start-btn mt-10"
						@click="submitControlComment"
						type="button"
						:disabled="isSubmittingControlComment || !['success_done', 'failed_done'].includes(homework_attempt.status) || !selectedReviewer || selectedReviewer !== homework_attempt.reviewer_id"
				>
					{{ homework_attempt.control ? 'Сохранить ответ' : 'Отправить ответ студенту' }}
				</button>
				<button v-if="homework_attempt.control && !isEditingControl" class="link-btn mt-10" @click="startEditingControl" type="button">
					Редактировать / дополнить ответ
				</button>
				<button v-if="isEditingControl" class="link-btn mt-10" @click="cancelEditingControl" type="button">
					Отмена
				</button>
				<p v-if="!['success_done', 'failed_done'].includes(homework_attempt.status)" class="button-info">
					Пожалуйста, установите и примените статус на "Не выполнено" или "Успешно выполнено" для активации кнопки
				</p>
				<p v-if="['success_done', 'failed_done'].includes(homework_attempt.status) && (!selectedReviewer || selectedReviewer !== homework_attempt.reviewer_id)" class="button-info">
					Пожалуйста, выберите и примените проверяющего перед отправкой ответа
				</p>

				<div v-if="homework_attempt.control && !isEditingControl" class="mt-20">
					<h3 class="label mb-5">Ваш ответ</h3>
					<div class="homework-rich-text" v-html="sanitizedControl"></div>
				</div>

				<template v-if="homework_attempt.reviewer_attachments.length">
					<h3 class="label mb-5 mt-20">Прикрепленные файлы</h3>
					<div class="files-preview-list">
						<div
							v-for="(attachment, index) in homework_attempt.reviewer_attachments"
							:key="index"
							class="files-preview-list__item"
						>
							<span class="files-preview-list__check icon icon--check icon--tiny icon--high-color" />
							<a :href="attachment.file_url_original" target="_blank" rel="noopener noreferrer" class="files-preview-list__name">{{ attachment.file_name }}</a>
						</div>
					</div>
				</template>
			</div>
		</section>
	</section>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import DOMPurify from 'dompurify';
import { linkifyText as linkifyTextUtil, linkifyHtml as linkifyHtmlUtil } from '../../../utils/linkify';
import 'ckeditor5/ckeditor5.css';

export default {
	name: 'HomeworkAttempt',

	props: {
		homework_attempt: { type: Object },
		reviewers: { type: Array },
		homework_attempt_comments_url: { type: String },
		homework_attempt_url: { type: String },
		list_statuses: { type: Array, required: true },
		previous_attempts: { type: Array }
	},

	data() {
		return {
			selectedReviewer: this.homework_attempt.reviewer_id || '',
			selectedCrossChecker: this.homework_attempt.cross_checker_id || '',
			selectedStatus: this.homework_attempt.status,
			statuses: this.list_statuses,
			editor: ClassicEditor,
			editorData: this.homework_attempt.control || '',
			localControl: this.homework_attempt.control || '',
			editorConfig: {
				toolbar: [
					'heading',
					'|',
					'bold',
					'italic',
					'link',
					'|',
					'bulletedList',
					'numberedList',
					'blockQuote',
					'|',
					'undo',
					'redo'
				],
				placeholder: 'Напишите здесь свой ответ',
				licenseKey: 'GPL',
				link: {
					decorators: {
						openInNewTab: {
							mode: 'manual',
							label: 'Открывать в новой вкладке',
							defaultValue: true,
							attributes: {
								target: '_blank',
								rel: 'noopener noreferrer'
							}
						}
					}
				}
			},
			attemptComment: '',
			isShowComments: false,
			isEditingControl: false,
			selectedSkipCount: this.homework_attempt.skip_count || false,
			selectedCustomCase: this.homework_attempt.custom_case,
			selectedNoReviewPayment: this.homework_attempt.no_review_payment || false,
			isSubmitFields: {
				reviewer_id: false,
				cross_checker_id: false,
				status: false,
				skip_count: false,
				custom_case: false,
				no_review_payment: false
			},
			isButtonDisabled: true,
			reviewerAttachments: [],
			isSubmittingControlComment: false,
			sanitizeOptions: {
				USE_PROFILES: { html: true },
				ADD_ATTR: ['target', 'rel', 'class', 'style', 'data-linktype', 'data-cke-tooltip-text'],
				FORBID_TAGS: ['style', 'script']
			}
		};
	},

	computed: {
		attemptTypeLabel() {
			return this.homework_attempt.attempt_type === 'rework' ? 'Доработка' : 'Первая попытка';
		},
		sanitizedControl() {
			return linkifyHtmlUtil(this.localControl || '');
		},
		isControlEditable() {
			return !this.homework_attempt.control || this.isEditingControl;
		}
	},

	mounted() {
		if (sessionStorage.isShowComments) {
			this.isShowComments = sessionStorage.isShowComments === 'true';
		}
	},

	watch: {
		isShowComments(newValue) {
			sessionStorage.isShowComments = newValue;
		},
		selectedReviewer(newVal) {
			this.checkChanges();
		},
		selectedCrossChecker(newVal) {
			this.checkChanges();
		},
		selectedStatus(newVal) {
			this.checkChanges();
		},
		selectedSkipCount(newVal) {
			this.checkChanges();
		},
		selectedCustomCase(newVal) {
			this.checkChanges();
		},
		selectedNoReviewPayment(newVal) {
			this.checkChanges();
		}
	},

	methods: {
		checkChanges() {
			const reviewerChosen = !!this.selectedReviewer;

			const reviewerChanged = this.selectedReviewer !== (this.homework_attempt.reviewer_id || '');
			const crossCheckerChanged = this.selectedCrossChecker !== (this.homework_attempt.cross_checker_id || '');
			const statusChanged = this.selectedStatus !== (this.homework_attempt.status || '');
			const skipCountChanged = this.selectedSkipCount !== !!this.homework_attempt.skip_count;
			const customCaseChanged = this.selectedCustomCase !== !!this.homework_attempt.custom_case;
			const noReviewPaymentChanged = this.selectedNoReviewPayment !== !!this.homework_attempt.no_review_payment;

			// Кнопка активна только если выбран и проверяющий, и есть изменения
			this.isButtonDisabled = !(
				reviewerChosen &&
				(reviewerChanged || crossCheckerChanged || statusChanged || skipCountChanged || customCaseChanged || noReviewPaymentChanged)
			);
		},

		formatDate(dateString) {
			const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
			return new Date(dateString).toLocaleDateString('ru-RU', options);
		},

		submitAttemptComment() {
			fetch(this.homework_attempt_comments_url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/javascript',
					'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
				},
				body: JSON.stringify({
					body: this.attemptComment,
				}),
			})
				.then(response => response.json())
				.then(comment => {
					this.homework_attempt.homework_attempt_comments.push(comment);
					this.attemptComment = '';
				})
				.catch((error) => {
					console.error('Error:', error);
				});
		},

		submitControlComment() {
			const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
			this.isSubmittingControlComment = true;
			const sanitizedControl = DOMPurify.sanitize(this.editorData || '', this.sanitizeOptions);

			if (this.reviewerAttachments.length) {
				const formData = new FormData();
				formData.append('homework_attempt[control]', sanitizedControl);

				this.reviewerAttachments.forEach((file, index) => {
					formData.append(`homework_attempt[reviewer_attachments_attributes][${index}][file]`, file);
				});

				fetch(this.homework_attempt_url, {
					method: 'PATCH',
					headers: { 'X-CSRF-Token': token },
					body: formData
				})
					.then(response => response.json())
					.then(data => {
						if (data.success) {
							this.homework_attempt.control = sanitizedControl;
							this.localControl = sanitizedControl;
							this.editorData = sanitizedControl;
							this.reviewerAttachments = [];
							this.showCheckSubmit('control');
							this.isEditingControl = false;
						} else {
							alert('Ошибка: ' + data.errors.join('; '));
						}
					})
					.catch(err => alert('Ошибка: ' + err.message))
					.finally(() => {
						this.isSubmittingControlComment = false;
					});
			} else {
				this.updateHomeworkAttempt('control', sanitizedControl);
				this.isSubmittingControlComment = false;
			}
		},

		submitAllChanges() {
			// Создаем объект payload для отправки всех изменений
			let payload = { homework_attempt: {} };

			// Добавляем данные в payload, если они изменены
			if (this.selectedReviewer !== this.homework_attempt.reviewer_id) {
				payload.homework_attempt['reviewer_id'] = this.selectedReviewer;
			}
			if (this.selectedCrossChecker !== this.homework_attempt.cross_checker_id) {
				payload.homework_attempt['cross_checker_id'] = this.selectedCrossChecker;
			}
			if (this.selectedStatus !== this.homework_attempt.status) {
				payload.homework_attempt['status'] = this.selectedStatus;
			}

			if (this.selectedSkipCount !== !!this.homework_attempt.skip_count) {
				payload.homework_attempt.skip_count = this.selectedSkipCount;
			}

			if (this.selectedCustomCase !== !!this.homework_attempt.custom_case) {
				payload.homework_attempt.custom_case = this.selectedCustomCase;
			}

			if (this.selectedNoReviewPayment !== !!this.homework_attempt.no_review_payment) {
				payload.homework_attempt.no_review_payment = this.selectedNoReviewPayment;
			}

			// Проверяем, есть ли изменения, которые нужно отправить
			if (Object.keys(payload.homework_attempt).length === 0) {
				alert('Нет изменений для сохранения.');
				return;
			}

			// Отправляем запрос
			fetch(this.homework_attempt_url, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/javascript',
					'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
				},
				body: JSON.stringify(payload),
			})
				.then(response => {
					if (!response.ok) {
						throw new Error('Network response was not ok');
					}
					return response.json();
				})
				.then(data => {
					if (data.success) {
						// Обновляем локальные данные напрямую
						if ('reviewer_id' in payload.homework_attempt) {
							this.homework_attempt.reviewer_id = this.selectedReviewer;
						}
						if ('cross_checker_id' in payload.homework_attempt) {
							this.homework_attempt.cross_checker_id = this.selectedCrossChecker;
						}
						if ('status' in payload.homework_attempt) {
							this.homework_attempt.status = this.selectedStatus;
						}

						if ('skip_count' in payload.homework_attempt) {
							this.homework_attempt.skip_count = this.selectedSkipCount;
						}

						if ('custom_case' in payload.homework_attempt) {
							this.homework_attempt.custom_case = this.selectedCustomCase;
						}

						if ('no_review_payment' in payload.homework_attempt) {
							this.homework_attempt.no_review_payment = this.selectedNoReviewPayment;
						}

						this.isButtonDisabled = true;

						// Показываем уведомление об успешном обновлении
						this.showCheckSubmit('all_fields');
						console.log('Все изменения успешно применены.');
					} else {
						console.error('Ошибка обновления:', data.errors.join('; '));
						alert('Ошибка обновления: ' + data.errors.join('; '));
					}
				})
				.catch(error => {
					console.error('Error:', error);
					alert('Ошибка: ' + error.message);
				});
		},

		showCheckSubmit(field) {
			this.isSubmitFields[field] = true;

			setTimeout(() => {
				this.isSubmitFields[field] = false;
			}, 3000);
		},

		updateHomeworkAttempt(field, value) {
			let payload = { homework_attempt: {}};
			payload["homework_attempt"][field] = value;

			fetch(this.homework_attempt_url, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/javascript',
					'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
				},
				body: JSON.stringify(payload),
			})
				.then(response => {
					if (!response.ok) {
						throw new Error('Network response was not ok');
					}
					return response.json();
				})
				.then(data => {
					if (data.success) {
						this.homework_attempt[field] = value;

						if (field === 'control') {
							this.editorData = value;
							this.localControl = value;
							this.isEditingControl = false;
						}

						this.showCheckSubmit(field);

						console.log(`${field} updated successfully`);
					} else {
						console.error(`Error updating ${field}:`, data.errors.join('; '));
						alert(`Ошибка обновления ${field}: ` + data.errors.join('; '));
					}
				})
				.catch((error) => {
					console.error('Error:', error);
					alert('Ошибка: ' + error.message);
				});
		},

		showComments() {
			this.isShowComments = !this.isShowComments;
		},

		linkifyText(text) {
			return linkifyTextUtil(text);
		},

		handleReviewerFileUpload(event) {
			this.reviewerAttachments = Array.from(event.target.files);
		},

		startEditingControl() {
			this.editorData = this.localControl || '';
			this.isEditingControl = true;
		},

		cancelEditingControl() {
			this.editorData = this.localControl || '';
			this.reviewerAttachments = [];
			this.isEditingControl = false;
		}
	}
}
</script>

<style scoped>
.button-info {
	color: #d9534f;
	font-size: 0.9em;
	margin-top: 5px;
}

/* Updated previous attempts styles */
.cd-hwa__prev-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.cd-hwa__prev-item {
  margin-bottom: 0.75em;
}
.cd-hwa__prev-badges {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;
}
.badge-skip {
  background-color: #6c757d;
  color: #fff;
}
.badge-status {
  background-color: #adb5bd;
  color: #212529;
}
.badge-sm {
  font-size: 0.75em;
  padding: 0.25em 0.5em;
  border-radius: 0.25em;
}
.toggle-switch {
	display: flex;
	align-items: center;
	cursor: pointer;
	user-select: none;
}

.toggle-switch input {
	position: absolute;
	opacity: 0;
	width: 0;
	height: 0;
}

.toggle-switch .slider {
	position: relative;
	flex-shrink: 0;
	width: 40px;
	height: 20px;
	background-color: #ccc;
	border-radius: 20px;
	transition: background 0.3s;
	margin-right: 8px;
}

.toggle-switch .slider::before {
	content: "";
	position: absolute;
	width: 16px;
	height: 16px;
	left: 2px;
	top: 2px;
	background-color: white;
	border-radius: 50%;
	transition: transform 0.3s;
}

.toggle-switch input:checked + .slider {
	background-color: #007bff;
}

.toggle-switch input:checked + .slider::before {
	transform: translateX(20px);
}

.toggle-switch .label-text {
	font-size: 0.9em;
	color: #333;
}

.labeled-field__input--rich {
	padding: 0;
	border: none;
	background: transparent;
}

.labeled-field__input--rich :deep(.ck-editor) {
	width: 100%;
}

.labeled-field__input--rich :deep(.ck-editor__editable) {
	min-height: 200px;
}

.labeled-field__input--rich :deep(.ck-editor__editable strong),
.labeled-field__input--rich :deep(.ck-editor__editable b) {
	font-weight: 700;
}

.homework-rich-text {
	font-size: 15px;
	line-height: 1.6;
	color: #1f1f1f;
}

.homework-rich-text :deep(strong),
.homework-rich-text :deep(b) {
	font-weight: 700;
}

.homework-rich-text :deep(h1),
.homework-rich-text :deep(h2),
.homework-rich-text :deep(h3),
.homework-rich-text :deep(h4),
.homework-rich-text :deep(h5),
.homework-rich-text :deep(h6) {
	font-weight: 600;
	margin-top: 1.2em;
	margin-bottom: 0.6em;
}

.homework-rich-text :deep(p) {
	margin-bottom: 0.75em;
}

.homework-rich-text :deep(ul),
.homework-rich-text :deep(ol) {
	margin: 0.75em 0;
	padding-left: 1.4em;
}

.homework-rich-text :deep(li) {
	margin-bottom: 0.35em;
}

.homework-rich-text :deep(a) {
	color: #0061d9;
	text-decoration: underline;
}

.homework-rich-text :deep(blockquote) {
	margin: 1em 0;
	padding-left: 1em;
	border-left: 3px solid #d0d7de;
	color: #4a4a4a;
	font-style: italic;
}
</style>
